import {
    CellClassParams,
    ColDef,
    ITooltipParams,
    ProcessDataFromClipboardParams,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import React from 'react';
import find from 'lodash/find';
import { Box, styled, useTheme } from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { useTranslation } from 'react-i18next';
import {
    useCreateMultipleUserContext,
    useCreateMultipleUserDispatchContext,
} from './context/CreateMultipleUserContext';
import {
    CreateMultipleUserActionType,
    UserCreationType,
} from './context/types';

import './CreateMultipleUsers.scss';
import WarningBanner from '../CreateOrEditUser/components/ui/WarningBanner';
import {
    emptyCellStyle,
    emptyTooltipValueGetter,
    usersListColumnDef,
    UserCreationTypeWithIndex,
    UserListError,
    validateUsersList,
    UCTKey,
    mergeUserAndDataFromColumn,
} from './helpers';
import { useGetDefaultUser } from './context/hooks';

const StyAgContainer = styled(Box, { name: 'StyAgContainer' })(({ theme }) => ({
    border: '1px solid lightgray',
    margin: '0.5rem',
    width: 'auto',
    height: '40vh',
    borderRadius: '5px',
    '.ag-header-cell': {
        backgroundColor: theme.palette.lightGrey,
    },
    '.ag-root-wrapper': {
        borderRadius: '5px',
    },
}));

const StyAddRowButton = styled(Box, { name: 'StyAddRowButton' })(
    ({ theme }) => ({
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.primary.main,
        fontSize: '1.2em',
        cursor: 'pointer',
        marginTop: '40px',
        marginBottom: '40px',
        marginLeft: '8px',
    }),
);

const CancelIconCellRenderer = () => <CancelIcon />;

type Props = {
    existingUsersEmails: string[];
    setFormIsValid: (valid: boolean) => void;
};

const CreateUsersList = ({ existingUsersEmails, setFormIsValid }: Props) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { users } = useCreateMultipleUserContext();
    const defaultUser = useGetDefaultUser();

    const usersWithIdx: UserCreationTypeWithIndex[] = users.map((u, i) => ({
        ...u,
        index: i,
    }));
    const errors = validateUsersList(
        usersWithIdx,
        existingUsersEmails,
        users.map((u) => u?.email).filter((e) => e !== undefined),
    );
    setFormIsValid(errors.length < 1);

    const dispatch = useCreateMultipleUserDispatchContext();

    const addUser = () => {
        dispatch({
            type: CreateMultipleUserActionType.addUser,
            user: defaultUser,
        });
    };

    const rmUser = (index: number) => {
        dispatch({ type: CreateMultipleUserActionType.removeUser, index });
    };

    const updateUser = (index: number, field: string, value: unknown) => {
        dispatch({
            type: CreateMultipleUserActionType.updateUser,
            index,
            user: { ...users[index], [field]: value },
        });
    };

    const cellStyle = (params: CellClassParams<UserCreationTypeWithIndex>) =>
        emptyCellStyle(params, errors, theme.palette.orange || 'orange');

    const tooltipValueGetter = (
        params: ITooltipParams<UserCreationTypeWithIndex>,
    ) =>
        emptyTooltipValueGetter(
            params,
            errors,
            t('adminSpace.createUsers.modalStepper.listSection.required'),
        );

    const columnDefs: ColDef[] = [
        {
            ...usersListColumnDef('gender', updateUser),
            maxWidth: 100,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
                values: [t('common.genderShort.M'), t('common.genderShort.F')],
            },
            valueGetter: (params) =>
                t(`common.genderShort.${params.data.gender}`),
            valueSetter: (params) => {
                updateUser(
                    params.data.index,
                    'gender',
                    params.newValue === t('common.genderShort.M') ? 'M' : 'F',
                );
                return true;
            },
        },
        usersListColumnDef('lastname', updateUser, 2),
        usersListColumnDef('firstname', updateUser, 2),
        usersListColumnDef('job', updateUser, 1),
        {
            ...usersListColumnDef('email', updateUser, 2),
            cellStyle: (
                params: CellClassParams<UserCreationTypeWithIndex>,
            ): { borderColor?: string } => {
                const error = find(
                    errors,
                    (err) =>
                        err.userIndex === params?.node?.data?.index &&
                        err.error !== UserListError.EMPTY,
                );
                if (error !== undefined) {
                    return { borderColor: theme.palette.red || 'red' };
                }
                return {};
            },
            tooltipValueGetter(
                params: ITooltipParams<UserCreationTypeWithIndex>,
            ) {
                const error = find(
                    errors,
                    (err) =>
                        err.userIndex === params?.node?.data?.index &&
                        err.error !== UserListError.EMPTY,
                );
                switch (error?.error) {
                    case UserListError.DUPLICATE_EMAIL: {
                        return t(
                            'adminSpace.createUsers.modalStepper.listSection.duplicatedEmail',
                        );
                    }
                    case UserListError.EXISTING_EMAIL: {
                        return t(
                            'adminSpace.createUsers.modalStepper.listSection.existingEmail',
                        );
                    }
                    case UserListError.INVALID_EMAIL: {
                        return t(
                            'adminSpace.createUsers.modalStepper.listSection.invalidEmail',
                        );
                    }
                    default:
                        return t('common.email');
                }
            },
        },
        {
            headerName: '',
            field: 'close',
            editable: false,
            width: 40,
            cellStyle: {
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            },
            cellClass: 'ag-close-cell',
            cellRenderer: CancelIconCellRenderer,
            onCellClicked: (param) => {
                if (param.rowIndex !== null) {
                    rmUser(param.rowIndex);
                }
            },
            resizable: false,
            suppressHeaderMenuButton: true,
            tooltipValueGetter: () =>
                t('adminSpace.createUsers.modalStepper.listSection.deleteLine'),
        },
    ];

    // Handle the paste operation.
    const processDataFromClipboard = (
        params: ProcessDataFromClipboardParams,
    ): string[][] | null => {
        const focusedCell = params.api.getFocusedCell();
        if (focusedCell === null) {
            return null;
        }
        const data = [...params.data];

        const focusedIndex = focusedCell.rowIndex;
        const columnId = focusedCell.column.getId() as UCTKey;

        const lastIdx = focusedIndex + params.data.length;
        const prevUsers = users.slice(0, focusedIndex);
        const nextUsers = users.slice(lastIdx);
        const replacedUsers = users.slice(focusedIndex, lastIdx);

        const newUsers: Partial<UserCreationType>[] = [];
        for (let i = 0; i < data.length; i += 1) {
            if (i < replacedUsers.length) {
                newUsers.push(
                    mergeUserAndDataFromColumn(
                        replacedUsers[i],
                        data[i],
                        columnId,
                    ),
                );
            } else {
                newUsers.push(
                    mergeUserAndDataFromColumn(
                        { gender: 'M' },
                        data[i],
                        columnId,
                    ),
                );
            }
        }

        dispatch({
            type: CreateMultipleUserActionType.replaceUsers,
            users: [...prevUsers, ...newUsers, ...nextUsers],
        });
        return null;
    };

    return (
        <>
            <StyAgContainer className="user-list ag-theme-material">
                <AgGridReact
                    key="ajout-grouper"
                    getRowId={({ data }: { data: UserCreationTypeWithIndex }) =>
                        `${data.index}`
                    }
                    rowData={usersWithIdx}
                    columnDefs={columnDefs}
                    defaultColDef={{
                        editable: true,
                        cellStyle,
                        tooltipValueGetter,
                    }}
                    processDataFromClipboard={processDataFromClipboard}
                    tooltipShowDelay={500}
                    columnMenu="legacy"
                    stopEditingWhenCellsLoseFocus
                />
            </StyAgContainer>
            <StyAddRowButton onClick={addUser}>
                <AddCircleIcon />
                <div>
                    {t(
                        'adminSpace.createUsers.modalStepper.listSection.addUser',
                    )}
                </div>
            </StyAddRowButton>
            {errors.length > 0 && (
                <WarningBanner
                    message={t(
                        'adminSpace.createUsers.modalStepper.listSection.formHasErrors',
                    )}
                />
            )}
        </>
    );
};

export default CreateUsersList;
